// ─────────────────────────────────────────────────────────────────────────────
// The audit worksheet as a Word document.
//
// WHY A FILE AT ALL. The screen had Print and nothing else, and Print is a browser
// dialogue — it produces paper, or a PDF named after the tab, and it cannot be
// attached to a QAPI binder, mailed to a corporate consultant, or kept as the
// facility's own copy of a cycle. "Where does it go after I save it?" was asked
// about this screen, and the honest answer was "into a database you cannot reach."
//
// WHY WORD AND NOT PDF. Print already yields a PDF through the browser. What a DON
// does not have is the editable document a facility pastes into its own QA template.
// docx is already loaded for the policy exports (window.docx, from index.html), so
// this adds no bundle weight and no new dependency.
//
// WHY ITS OWN MODULE. It is built from the SAME merged rows the screen renders and
// the print sheet prints — passed in, never refetched. A document assembled from a
// second read of the record is a document that can disagree with the screen it was
// downloaded from, which is the failure the print sheet's own comment describes.
//
// WHAT IT REFUSES TO DO QUIETLY. An unsigned worksheet is marked DRAFT on every page
// and in its filename. A downloaded file outlives the screen that made it: a
// half-finished audit, printed and filed, is indistinguishable from a completed one
// once it is on paper, and this is a document a surveyor may be handed.
// ─────────────────────────────────────────────────────────────────────────────

/** What a stored state code reads as in a document, where a coloured chip cannot. */
const STATE_WORD = { Y: "Yes", N: "No", NA: "N/A", "?": "Not checked" };
export const stateWord = (s) => STATE_WORD[s] || "Not checked";

/** Date only, from either a date string or a timestamp. Never a locale surprise. */
const day = (v) => (v ? String(v).slice(0, 10) : "");

/**
 * The filename. CCN, resident, aspect and date, so a folder of these sorts usefully
 * and two residents' sheets cannot overwrite each other.
 */
export function auditFileName({ ccn, residentName, residentId, aspectTitle, reviewedOn, signed }) {
  const safe = (v, n = 40) =>
    String(v || "")
      .replace(/[^a-z0-9\-_ ]+/gi, "")
      .trim()
      .replace(/\s+/g, "-")
      .slice(0, n)
      .replace(/-+$/, "");
  const parts = [
    safe(ccn, 10) || "facility",
    safe(residentName || `resident-${residentId}`, 40) || "resident",
    safe(aspectTitle, 40) || "audit",
    day(reviewedOn) || new Date().toISOString().slice(0, 10),
  ];
  // DRAFT IS IN THE NAME, not only in the document. The name is what someone sees in
  // an inbox before they open it.
  if (!signed) parts.push("DRAFT");
  return `${parts.join("_")}.docx`;
}

/**
 * Build the .docx.
 *
 * @param {object} p
 * @param {object} p.d           the worksheet payload the screen is rendering
 * @param {string} p.aspectTitle the title the SCREEN shows — passed, not recomputed
 * @param {object} p.coverage    the live coverage the screen's headline is computed from
 * @returns {Promise<Blob>}
 */
export async function buildAuditDocx({
  d,
  aspectTitle,
  ccn,
  residentId,
  residentName,
  coverage,
  reviewer,
  reviewedOn,
  comments,
  signed,
}) {
  if (!window.docx) throw new Error("Word export library not loaded. Please refresh the page and try again.");
  const { Document, Packer, Paragraph, TextRun, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle, Header } =
    window.docx;

  const indicators = d?.indicators || [];
  const rowsByN = new Map((d?.rows || []).map((r) => [r.n, r]));
  const c = coverage || {};
  const pct = c.compliance == null ? null : Math.round(c.compliance * 1000) / 10;

  const text = (t, o = {}) => new TextRun({ text: String(t ?? ""), font: "Calibri", size: 20, ...o });
  const para = (runs, o = {}) => new Paragraph({ children: Array.isArray(runs) ? runs : [runs], spacing: { after: 80 }, ...o });

  const cell = (children, o = {}) =>
    new TableCell({
      children: Array.isArray(children) ? children : [children],
      margins: { top: 60, bottom: 60, left: 90, right: 90 },
      ...o,
    });

  const headerRow = new TableRow({
    tableHeader: true, // repeats on every page -- the table runs past one
    children: ["#", "Indicator", "Where it is answered from", "Record", "Reviewer", "Reads as", "What was found"].map((h) =>
      cell(para(text(h, { bold: true, size: 18 })), { shading: { fill: "E8EEF7" } }),
    ),
  });

  const bodyRows = indicators.map((ind) => {
    const row = rowsByN.get(ind.n) || {};
    const sys = row.system?.state ?? null;
    const ent = row.entered?.state ?? null;
    const note = row.entered?.note || "";
    const by = row.entered?.by ? `${row.entered.by}${row.entered.at ? ` · ${day(row.entered.at)}` : ""}` : "";
    return new TableRow({
      children: [
        cell(para(text(ind.n))),
        cell(para(text(ind.text, { bold: true }))),
        // WHERE A ROW IS ANSWERED FROM TRAVELS WITH IT. "not readable by software" is
        // the difference between a row nobody checked and a row nothing COULD check,
        // and on paper it is the only thing that tells them apart.
        cell(para(text(ind.source || "—", { size: 18, color: "555555" }))),
        cell(para(text(sys ? stateWord(sys) : "—"))),
        cell(para(text(ent ? stateWord(ent) : "—"))),
        cell(para(text(stateWord(row.effective), { bold: true }))),
        cell([
          para(text(note || "—")),
          ...(by ? [para(text(`Entered by ${by}`, { size: 16, color: "777777" }))] : []),
          // The row where the two authors disagree is the most interesting row on the
          // sheet, and a document that drops it is a document that reads cleaner than
          // the audit was.
          ...(row.disagrees
            ? [para(text(`Reviewer's answer differs from the record (${stateWord(sys)} → ${stateWord(ent)}).`, { size: 16, italics: true, color: "9A3412" }))]
            : []),
        ]),
      ],
    });
  });

  const table = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    columnWidths: [400, 2600, 1600, 900, 900, 900, 2700],
    rows: [headerRow, ...bodyRows],
    borders: {
      top: { style: BorderStyle.SINGLE, size: 2, color: "C8D2E0" },
      bottom: { style: BorderStyle.SINGLE, size: 2, color: "C8D2E0" },
      left: { style: BorderStyle.SINGLE, size: 2, color: "C8D2E0" },
      right: { style: BorderStyle.SINGLE, size: 2, color: "C8D2E0" },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: "DCE3EC" },
      insideVertical: { style: BorderStyle.SINGLE, size: 2, color: "DCE3EC" },
    },
  });

  // THE PERCENTAGE NEVER APPEARS WITHOUT ITS DENOMINATOR. Same rule the screen is
  // held to by post-incident-audit.test.js, and it matters more here: this page can
  // be photocopied away from everything that qualifies it.
  const headline =
    pct == null
      ? "No percentage can be computed — no row has been answered yet."
      : `${pct}% of ${c.verified ?? 0} answered row(s). The ${Math.round((d?.threshold ?? 0.95) * 100)}% threshold applies to all ${c.total ?? indicators.length} indicators.`;

  const doc = new Document({
    creator: "SurvAIHealth",
    title: `${aspectTitle} — ${residentName || residentId}`,
    sections: [
      {
        properties: { page: { margin: { top: 720, bottom: 720, left: 640, right: 640 } } },
        headers: signed
          ? undefined
          : {
              // ON EVERY PAGE, not just the first. Pages get separated.
              default: new Header({
                children: [
                  para(text("DRAFT — this audit has not been signed off and is not the facility's record.", {
                    bold: true, color: "B45309", size: 18,
                  }), { alignment: AlignmentType.RIGHT }),
                ],
              }),
            },
        children: [
          para(text("Performance Improvement Monitoring Worksheet", { bold: true, size: 30, color: "0F172A" })),
          para(text(aspectTitle, { bold: true, size: 24, color: "1E40AF" })),
          para([
            text("Resident: ", { bold: true }), text(residentName || `Resident ${residentId}`),
            text("   ·   Facility (CCN): ", { bold: true }), text(ccn || "—"),
          ]),
          para([
            text("Method of review: ", { bold: true }), text(d?.methodOfReview || "—"),
            text("   ·   Record read: ", { bold: true }), text(day(d?.compiled?.asOf) || "—"),
          ]),
          para([
            text("Reviewer: ", { bold: true }), text(reviewer || "________________"),
            text("   ·   Date reviewed: ", { bold: true }), text(day(reviewedOn) || "____________"),
            text("   ·   Status: ", { bold: true }),
            text(signed ? "Signed off" : "DRAFT — not signed off", { bold: true, color: signed ? "15803D" : "B45309" }),
          ]),
          para(text(headline, { bold: true, size: 22 }), { spacing: { before: 160, after: 60 } }),
          // WHO ANSWERED WHAT. "9 of 11 met" means two different things depending on
          // whether software verified them or a person attested to them, and the
          // screen is required to say which. So is the file.
          para(text(
            `${c.fromData ?? 0} verified from the record · ${c.fromPerson ?? 0} entered by a reviewer · ` +
            `${c.unknown ?? 0} still to check · ${c.disagreements ?? 0} where the reviewer differs from the record.`,
            { size: 18, color: "555555" },
          ), { spacing: { after: 200 } }),
          table,
          para(text("Comments", { bold: true, size: 22 }), { spacing: { before: 240, after: 60 } }),
          para(text(comments || "—")),
          para(text(
            signed
              ? "Signed off. This worksheet is the record of that cycle and cannot be edited — not by anyone, and not by the nightly scan."
              : "This worksheet is still open. Answers may still change, and nothing here is the facility's record until it is signed off in SurvAIHealth.",
            { size: 16, italics: true, color: "777777" },
          ), { spacing: { before: 200 } }),
        ],
      },
    ],
  });

  return Packer.toBlob(doc);
}

/** Hand the blob to the browser. Mirrors downloadPolicyBlob in App.js. */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  // Revoked late: revoking immediately races the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
